"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { Suspense } from "react";

const STEPS = ["Plan", "Details", "Verify"];

const PLANS = [
  {
    id: "artist",
    name: "Artist",
    description: "For independent artists distributing music videos.",
    features: [
      "1 ArtistVEVO channel",
      "Music video distribution",
      "Vevo analytics",
    ],
    highlight: false,
  },
  {
    id: "podcast",
    name: "Podcast",
    description: "For podcasters distributing video content.",
    features: [
      "1 ArtistVEVO channel",
      "Podcast video distribution",
      "Vevo analytics",
    ],
    highlight: false,
  },
  {
    id: "label",
    name: "Label",
    description: "For record labels managing multiple artists.",
    features: [
      "Unlimited ArtistVEVO channels",
      "Full label management",
      "Priority support",
    ],
    highlight: true,
  },
];

function PlanPicker() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Referral code from a shared link — threaded through to the register step,
  // where it's validated server-side and turned into a prefilled promo code.
  const ref = searchParams.get("ref");

  return (
    <div>
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-0 mb-10">
        {STEPS.map((step, i) => (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  i === 0
                    ? "border-yellow-400 bg-yellow-400 text-black"
                    : "border-zinc-700 bg-zinc-900 text-zinc-600"
                }`}
              >
                {i + 1}
              </div>
              <span className={`text-xs mt-1.5 ${i === 0 ? "text-yellow-400" : "text-zinc-600"}`}>
                {step}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="w-16 h-px bg-zinc-800 mx-2 mb-5" />
            )}
          </div>
        ))}
      </div>

      <div className="mb-7 text-center">
        <h1 className="text-2xl font-bold text-white">Choose your plan</h1>
        <p className="text-zinc-400 mt-1.5 text-sm">
          Select a plan to get started. You&apos;ll enter your details on the next step.
        </p>
        {ref && (
          <p className="mt-3 inline-block text-xs font-medium px-3 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-400">
            Referral code applied — your discount is added at checkout
          </p>
        )}
      </div>

      <div className="space-y-3">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-zinc-900 border rounded-xl p-5 transition ${
              plan.highlight
                ? "border-yellow-400/40 ring-1 ring-yellow-400/20"
                : "border-zinc-800"
            }`}
          >
            {plan.highlight && (
              <span className="absolute -top-2.5 left-5 bg-yellow-400 text-black text-xs font-bold px-2.5 py-0.5 rounded-full">
                Most Popular
              </span>
            )}
            <div className="mb-3">
              <h2 className="text-base font-semibold text-white">{plan.name}</h2>
              <p className="text-zinc-400 text-sm mt-0.5">{plan.description}</p>
            </div>
            <ul className="space-y-1.5 mb-4">
              {plan.features.map((f) => (
                <li key={f} className="text-sm text-zinc-300 flex items-center gap-2">
                  <span className="text-yellow-400 flex-shrink-0">✓</span> {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() =>
                router.push(
                  `/signup/register?plan=${plan.id}${ref ? `&ref=${encodeURIComponent(ref)}` : ""}`
                )
              }
              className={`w-full font-semibold py-2.5 rounded-lg transition text-sm ${
                plan.highlight
                  ? "bg-yellow-400 text-black hover:bg-yellow-300"
                  : "bg-zinc-800 text-white hover:bg-zinc-700"
              }`}
            >
              Select {plan.name}
            </button>
          </div>
        ))}
      </div>

      <p className="mt-5 text-center text-xs text-zinc-600">
        Payments are processed securely. Cancel anytime.
      </p>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" /></div>}>
      <PlanPicker />
    </Suspense>
  );
}
